// src/routes/SelectInput/InputSources/Microphone.ts

import { captureException } from '@sentry/react';
import { range } from 'es-toolkit';
import { getInputId } from '~/modules/Players/utils';
import userMediaService from '~/modules/UserMedia/userMediaService';
import { InputSource } from './interfaces';

interface NameMapper {
  test: (label: string, channel: number, channels: number) => boolean;
  map: (label: string, channel: number) => string;
}

const micDebugEnabled = () =>
  (typeof window !== 'undefined' && window.localStorage?.getItem('AK_MIC_DEBUG') === '1') || false;

const micLog = (...args: unknown[]) => {
  if (!micDebugEnabled()) return;

  console.debug('[fastKaraoke mic]', ...args);
};

const singstarWirelessMicMapper: NameMapper = {
  test: (label) =>
    label.toLowerCase().includes('wireless mic #') || label.toLowerCase().includes('sony wireless singstar'),
  map: (_, channel) => `SingStar Wireless - ${channel === 0 ? 'Blue' : 'Red'}`,
};
const singstarWiredMicMapper: NameMapper = {
  test: (label) => label.toLowerCase().includes('usbmic serial#') || label.toLowerCase().includes('singstar'),
  map: (_, channel) => `SingStar Wired - ${channel === 0 ? 'Blue' : 'Red'}`,
};
const singstarGenericMapper: NameMapper = {
  test: (label, _, channels) => channels === 2 && label.toLowerCase().includes('singstar'),
  map: (_, channel) => `SingStar Mic - ${channel === 0 ? 'Blue' : 'Red'}`,
};

const getPreferred = (...[label, ch, channels]: Parameters<NameMapper['test']>) =>
  singstarWiredMicMapper.test(label, ch, channels) ||
  singstarWirelessMicMapper.test(label, ch, channels) ||
  singstarGenericMapper.test(label, ch, channels)
    ? ch
    : undefined;

const mapInputName = (label: string, channel: number, channels: number) => {
  if (singstarWirelessMicMapper.test(label, channel, channels)) return singstarWirelessMicMapper.map(label, channel);
  if (singstarWiredMicMapper.test(label, channel, channels)) return singstarWiredMicMapper.map(label, channel);
  if (singstarGenericMapper.test(label, channel, channels)) return singstarGenericMapper.map(label, channel);

  return channels > 1 ? `${label} (ch ${channel + 1})` : label;
};

// ✅ Heurística: solo SingStar tratamos como 2ch
const guessChannels = (label: string) => {
  const l = (label ?? '').toLowerCase();
  if (
    l.includes('singstar') ||
    l.includes('usbmic serial#') ||
    l.includes('wireless mic #') ||
    l.includes('sony wireless singstar')
  ) {
    return 2;
  }
  return 1;
};

export class MicrophoneInputSource {
  private static inputList: InputSource[] = [];
  public static readonly inputName = 'Microphone';

  public static getDefault = () =>
    MicrophoneInputSource.inputList.find((input) => input.id === getInputId({ deviceId: 'default', channel: 0 })) ??
    MicrophoneInputSource.inputList[0] ??
    null;

  public static getInputs = async (): Promise<InputSource[]> => {
    let devices: MediaDeviceInfo[] = [];

    try {
      micLog('Requesting mic permission (for enumerateDevices)...');
      await userMediaService.getUserMedia({ audio: true, video: false });

      devices = await userMediaService.enumerateDevices();

      micLog(
        'Enumerated devices:',
        devices.map((d) => ({ kind: d.kind, label: d.label, deviceId: d.deviceId })),
      );
    } catch (e) {
      captureException(e, { level: 'warning', extra: { message: 'Microphone.getInputs' } });
      console.warn(e);
    }

    const inputList = devices
      .filter((device) => device.kind === 'audioinput')
      .flatMap((device) => {
        const channels = guessChannels(device.label);

        // ⚠️ Si quieres seguir “probando” canales para depurar, hazlo SOLO en debug:
        // if (micDebugEnabled()) { ...probe... }  (pero no lo recomiendo para runtime)

        return range(0, channels).map((channel) => ({
          label: mapInputName(device.label, channel, channels),
          channel,
          channels,
          deviceId: device.deviceId,
          id: getInputId({ deviceId: device.deviceId, channel }),
          preferred: getPreferred(device.label, channel, channels),
        }));
      });

    MicrophoneInputSource.inputList = inputList;

    micLog('Built microphone input list:', MicrophoneInputSource.inputList);

    return MicrophoneInputSource.inputList;
  };

  public static subscribeToListChange = (callback: () => void) =>
    navigator.mediaDevices.addEventListener('devicechange', callback);
  public static unsubscribeToListChange = (callback: () => void) =>
    navigator.mediaDevices.removeEventListener('devicechange', callback);
}
